
import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, Zap, Database, ShieldAlert, Globe } from 'lucide-react';
import { Order, SystemConfig } from '../types';
import { format } from 'date-fns';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  config: SystemConfig;
  orders: Order[];
  onOpenSettings: () => void;
}

export const NotificationCenter: React.FC<Props> = ({ isOpen, onClose, config, orders, onOpenSettings }) => {

  const auditLogs = useMemo(() => {
    const logs: any[] = [];
    const now = Date.now();
    const FOUR_HOURS = 4 * 60 * 60 * 1000;

    const recentOrders = (orders || []).filter(o => (now - (o.statusUpdatedAt || o.createdAt || now)) < FOUR_HOURS);

    recentOrders.forEach(o => {
      if (o.source === 'CARDAPIO_WEB') {
        const label = o.shortCode ? `#${o.shortCode}` : (o.displayId ? `#${o.displayId}` : 'PDV');

        logs.push({
          id: `audit_${o.id}_${o.status}`,
          type: 'info',
          title: `Pedido ${label}: ${o.status.toUpperCase()}`,
          message: `Atualização via ${o.salesChannel}. GPS: ${o.geocodingFailed ? 'Ponto Hub' : 'Cache OK'}.`,
          icon: Database,
          time: o.statusUpdatedAt || o.createdAt || now
        });
      }
    });

    return logs.sort((a, b) => b.time - a.time).slice(0, 30);
  }, [orders]);

  if (!isOpen) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[120000]" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, y: -10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -10, scale: 0.95 }}
        className="fixed top-20 right-4 w-[400px] bg-card-bg/95 backdrop-blur-3xl border border-border-dim rounded-[2.5rem] shadow-[var(--shadow-xl)] z-[120001] overflow-hidden flex flex-col max-h-[80vh]"
      >
        <div className="p-6 bg-card-bg/50 border-b border-border-dim flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Bell className="w-5 h-5 text-sal-cyber-teal" />
            <div className="flex flex-col">
              <span className="text-sm font-black text-text-primary uppercase-ops tracking-widest">Central Sofia</span>
              <span className="text-[10px] font-bold text-sal-cyber-teal uppercase-ops tracking-[0.3em]">Auditoria Logística</span>
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Fechar notificações" className="p-2 text-text-muted hover:text-text-primary transition-all bg-black/5 dark:bg-white/5 rounded-xl">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto no-scrollbar p-4 space-y-3 bg-gradient-to-b from-transparent to-black/5 dark:to-black/30">
          {auditLogs.length === 0 ? (
            <div className="py-20 text-center flex flex-col items-center opacity-40">
              <Globe className="w-12 h-12 mb-4 text-icon-muted" />
              <p className="text-xs font-black uppercase-ops tracking-widest text-text-muted">Sem eventos recentes</p>
            </div>
          ) : (
            auditLogs.map(log => (
              <div key={log.id} className="w-full text-left p-4 rounded-[1.5rem] border bg-card-bg/50 border-border-dim flex gap-4 transition-all hover:bg-card-bg/80">
                <div className={`p-2.5 rounded-2xl shrink-0 h-fit bg-sal-cyber-teal/5 text-sal-cyber-teal`}>
                  <log.icon className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex justify-between items-start mb-1">
                    <h4 className="text-xs font-black uppercase-ops tracking-wider text-text-primary">{log.title}</h4>
                    <span className="text-[10px] font-bold text-text-muted font-mono">{format(log.time, 'HH:mm:ss')}</span>
                  </div>
                  <p className="text-xs text-text-muted leading-relaxed font-medium line-clamp-2">{log.message}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </motion.div>
    </>,
    document.body
  );
};
